import type { TripViewModel } from 'flights/tripViewModel';

import {
  isTripViewModelWithinSelectedTimeBlocksFilter,
  checkIfTripViewModelContainsSelectedLegs,
} from '@helpers/flights/flightFilters';

export function filterMultiCityTripViewModelBasedOnFilters({
  tripViewModels,
  selectedStopCodes,
  selectedFlightExperiences,
  selectedAirlineCodes,
  selectedStopoverAirportCodes,
  selectedStopoverDuration,
  selectedDepartDuration,
  selectedDepartLegDepartureTimeBlocks,
  selectedDepartLegArrivalTimeBlocks,
  selectedUsdPrices,
  priceOptions,
  selectedLegs,
}: {
  tripViewModels: Array<TripViewModel>;
  selectedStopCodes: Array<FlightMulticityFilterState<number>>; // 0, 1, 2
  selectedFlightExperiences: Array<FlightMulticityFilterState<string>>;
  selectedAirlineCodes: Array<FlightMulticityFilterState<string>>;
  selectedStopoverAirportCodes: Array<FlightMulticityFilterState<string>>;
  selectedStopoverDuration: Array<FlightMulticityFilterState<number>>;
  selectedDepartDuration: Array<FlightMulticityFilterState<number>>;
  selectedDepartLegDepartureTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>;
  selectedDepartLegArrivalTimeBlocks: Array<FlightMulticityFilterState<FlightFilterTimeBlock>>;
  selectedUsdPrices: Array<number>;
  priceOptions: PriceOptions;
  selectedLegs: Array<{ index: number; legId: string }>;
}): Array<TripViewModel> {
  // Filter for multicity
  return tripViewModels.filter((tripViewModel) => {
    const fareViewModels = tripViewModel.filteredFares;

    const stopCodesFilter = applyStopCodesFilterMulticity(selectedStopCodes, tripViewModel);

    const flightExperienceFilter = applyFlightExperienceFilterMulticity(
      selectedFlightExperiences,
      tripViewModel,
    );

    const airlineCodesFilter = applyAirlineCodesFilterMulticity(
      selectedAirlineCodes,
      tripViewModel,
    );

    const stopoverAirportCodesFilter = applyStopoverAirportCodesFilterMulticity(
      selectedStopoverAirportCodes,
      tripViewModel,
    );

    const durationFilters = applyDurationFiltersMulticity(
      selectedStopoverDuration,
      selectedDepartDuration,
      tripViewModel,
    );

    const flightTimeFilter = applyFlightTimeFilterMulticity(
      selectedDepartLegDepartureTimeBlocks,
      selectedDepartLegArrivalTimeBlocks,
      tripViewModel,
    );

    const priceFilter = applyPriceFilterMulticity(
      selectedUsdPrices,
      fareViewModels,
      priceOptions.showTotalPrice,
    );

    const legFilter = checkIfTripViewModelContainsSelectedLegs(tripViewModel, selectedLegs);

    return (
      priceFilter &&
      stopCodesFilter &&
      flightExperienceFilter &&
      airlineCodesFilter &&
      stopoverAirportCodesFilter &&
      durationFilters &&
      flightTimeFilter &&
      legFilter
    );
  });
}

export const applyStopCodesFilterMulticity = (
  selectedStopCodes: FlightMulticityFilterState<number>[],
  tripViewModel: TripViewModel,
) => {
  const stopCodesFilteredValues = selectedStopCodes.map((legFilter) => {
    if (legFilter.values.length > 0) {
      return legFilter.values.includes(tripViewModel.legs[legFilter.leg].leg.stopoversCount);
    }

    return true;
  });
  return stopCodesFilteredValues.every(Boolean);
};

export const applyFlightExperienceFilterMulticity = (
  selectedFlightExperiences: FlightMulticityFilterState<string>[],
  tripViewModel: TripViewModel,
) => {
  const isNotOvernightFlightFilteredValues = selectedFlightExperiences.map((legFilter) => {
    if (legFilter.values.includes('no_overnight_flight')) {
      return !tripViewModel.legs[legFilter.leg].leg.overnight;
    }

    return true;
  });
  const isNotOvernightFlightFilter = isNotOvernightFlightFilteredValues.every(Boolean);

  const isNotLongStopoverFlight = selectedFlightExperiences.map((legFilter) => {
    if (legFilter.values.includes('no_long_stopover')) {
      return !tripViewModel.legs[legFilter.leg].leg.longStopover;
    }

    return true;
  });
  const isNotLongStopoverFlightFilter = isNotLongStopoverFlight.every(Boolean);

  return isNotOvernightFlightFilter && isNotLongStopoverFlightFilter;
};

export const applyAirlineCodesFilterMulticity = (
  selectedAirlineCodes: FlightMulticityFilterState<string>[],
  tripViewModel: TripViewModel,
) => {
  const airlineCodesFilteredValues = selectedAirlineCodes.map((legFilter) => {
    if (legFilter.values.length > 0) {
      return legFilter.values.some((airlineCode) =>
        tripViewModel.legs[legFilter.leg].leg.airlineCodes
          .map((code) => code.toLowerCase())
          .includes(airlineCode.toLowerCase()),
      );
    }

    return true;
  });
  return airlineCodesFilteredValues.every(Boolean);
};

export const applyStopoverAirportCodesFilterMulticity = (
  selectedStopoverAirportCodes: FlightMulticityFilterState<string>[],
  tripViewModel: TripViewModel,
) => {
  const stopoverAirportCodesFilteredValues = selectedStopoverAirportCodes.map((legFilter) => {
    if (legFilter.values.length > 0) {
      return legFilter.values.some((stopoverAirportCode) =>
        tripViewModel.legs[legFilter.leg].leg.stopoverAirportCodes.includes(stopoverAirportCode),
      );
    }

    return true;
  });
  return stopoverAirportCodesFilteredValues.every(Boolean);
};

export const applyDurationFiltersMulticity = (
  selectedStopoverDuration: FlightMulticityFilterState<number>[],
  selectedDepartDuration: FlightMulticityFilterState<number>[],
  tripViewModel: TripViewModel,
) => {
  const stopoverDurationFilteredValues = selectedStopoverDuration.map((legFilter) => {
    if (legFilter.values.length === 2) {
      const stopoverValues = tripViewModel.legs[legFilter.leg].leg.stopoverDurationMinutes;
      return stopoverValues >= legFilter.values[0] && stopoverValues <= legFilter.values[1];
    }

    return true;
  });
  const stopoverDuration = stopoverDurationFilteredValues.every(Boolean);

  const departDurationFilteredValues = selectedDepartDuration.map((legFilter) => {
    if (legFilter.values.length === 2) {
      const departDurationValues = tripViewModel.legs[legFilter.leg].leg.durationMinutes;
      return (
        departDurationValues >= legFilter.values[0] && departDurationValues <= legFilter.values[1]
      );
    }

    return true;
  });
  const departDurationFilter = departDurationFilteredValues.every(Boolean);

  return stopoverDuration && departDurationFilter;
};

export const applyFlightTimeFilterMulticity = (
  selectedDepartLegDepartureTimeBlocks: FlightMulticityFilterState<FlightFilterTimeBlock>[],
  selectedDepartLegArrivalTimeBlocks: FlightMulticityFilterState<FlightFilterTimeBlock>[],
  tripViewModel: TripViewModel,
) => {
  const departLegDepartureTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
    tripViewModel,
    selectedDepartLegDepartureTimeBlocks,
    'departLegDepartureTime',
  );

  const departLegArrivalTimeBlockFilter = isTripViewModelWithinSelectedTimeBlocksFilter(
    tripViewModel,
    selectedDepartLegArrivalTimeBlocks,
    'departLegArrivalTime',
  );

  return departLegDepartureTimeBlockFilter && departLegArrivalTimeBlockFilter;
};

export const applyPriceFilterMulticity = (
  selectedUsdPrices: number[],
  fareViewModels: FareViewModel[],
  isShowingTotalPrice: boolean,
) => {
  let priceFilter = true;
  if (selectedUsdPrices.length === 2) {
    const fare = fareViewModels[0].fare;

    if (fare) {
      const { amountUsd, totalAmountUsd } = fare.price;

      let calculatedAmount = 0;

      if (isShowingTotalPrice) {
        calculatedAmount = totalAmountUsd;
      } else {
        calculatedAmount = amountUsd;
      }

      priceFilter =
        !!fare &&
        calculatedAmount >= selectedUsdPrices[0] &&
        calculatedAmount <= selectedUsdPrices[1];
    }
  }

  return priceFilter;
};
